/**
 * Centralized logging utility for Velostruct.
 * Logs to console AND to a persistent in-memory buffer that can be viewed in the UI.
 */

export interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  source: string;
  message: string;
  data?: unknown;
  stack?: string;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 500; // Keep last 500 logs in memory
  private listeners: Set<(logs: LogEntry[]) => void> = new Set();

  log(level: LogEntry['level'], source: string, message: string, data?: unknown, stack?: string) {
    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      source,
      message,
      data,
      stack,
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console output with color coding
    const prefix = `[${source}]`;
    const timestamp = new Date().toLocaleTimeString();
    const color = level === 'error' ? '#ef4444' : level === 'warn' ? '#f59e0b' : level === 'debug' ? '#6b7280' : '#10b981';
    
    if (level === 'error') {
      console.error(`%c${timestamp} ${prefix}`, `color: ${color}; font-weight: bold;`, message, data ?? '');
    } else if (level === 'warn') {
      console.warn(`%c${timestamp} ${prefix}`, `color: ${color}; font-weight: bold;`, message, data ?? '');
    } else {
      console.log(`%c${timestamp} ${prefix}`, `color: ${color}; font-weight: bold;`, message, data ?? '');
    }

    if (stack) {
      console.error(stack);
    }

    // Notify listeners
    this.listeners.forEach(listener => listener(this.logs));
  }

  info(source: string, message: string, data?: unknown) {
    this.log('info', source, message, data);
  }

  warn(source: string, message: string, data?: unknown) {
    this.log('warn', source, message, data);
  }

  error(source: string, message: string, data?: unknown, error?: Error) {
    this.log('error', source, message, data, error?.stack);
  }

  debug(source: string, message: string, data?: unknown) {
    this.log('debug', source, message, data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  clearLogs() {
    this.logs = [];
    this.listeners.forEach(listener => listener(this.logs));
  }

  subscribe(listener: (logs: LogEntry[]) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  exportLogs(): string {
    return this.logs.map(log => 
      `${log.timestamp} [${log.level.toUpperCase()}] ${log.source}: ${log.message}${log.data ? ' ' + JSON.stringify(log.data) : ''}`
    ).join('\n');
  }
}

export const logger = new Logger();

// Expose globally for debugging
(window as any).__velostruct_logger = logger;
